import * as yup from 'yup'
import { IntlShape, defineMessages } from 'react-intl'
import { IDriverPersonalInformationFormValues } from 'features/Drivers/types'
import { isEmpty } from 'lodash'
import { EMAIL_LENGTH } from 'utils/schema'

const firstNameMessages = defineMessages({
  required: {
    id: 'drivers.form.firstName.required',
    defaultMessage: 'Driver first name is required'
  },
  max: {
    id: 'drivers.form.firstName.max',
    defaultMessage: 'Driver first name must be at most {max} characters'
  }
})

const lastNameMessages = defineMessages({
  required: {
    id: 'drivers.form.lastName.required',
    defaultMessage: 'Driver last name is required'
  },
  max: {
    id: 'drivers.form.lastName.max',
    defaultMessage: 'Driver last name must be at most {max} characters'
  }
})

const partnerMessages = defineMessages({
  required: {
    id: 'drivers.form.partner.required',
    defaultMessage: 'Partner is required'
  }
})

export const phoneMessages = defineMessages({
  required: {
    id: 'drivers.form.phone.required',
    defaultMessage: 'Phone is required'
  },
  format: {
    id: 'drivers.form.phone.format',
    defaultMessage: 'Phone must be {length} characters'
  }
})

export const emailMessages = defineMessages({
  required: {
    id: 'drivers.form.email.required',
    defaultMessage: 'Email is required'
  },
  format: {
    id: 'drivers.form.email.format',
    defaultMessage: 'Email must be a valid email'
  },
  min: {
    id: 'drivers.form.email.min',
    defaultMessage: 'Email must be at least {min} characters'
  },
  max: {
    id: 'drivers.form.email.max',
    defaultMessage: 'Email must be at most {max} characters'
  }
})

export const driverPersonaInformationSchema = (intl: IntlShape): yup.SchemaOf<IDriverPersonalInformationFormValues> => {
  const validations = {
    firstName: yup
      .string()
      .required(intl.formatMessage(firstNameMessages.required))
      .max(128, ({ max }) => intl.formatMessage(firstNameMessages.max, { max })),
    lastName: yup
      .string()
      .required(intl.formatMessage(lastNameMessages.required))
      .max(128, ({ max }) => intl.formatMessage(lastNameMessages.max, { max })),
    partner: yup.object().shape({
      name: yup.string().required(),
      id: yup.string().required(intl.formatMessage(partnerMessages.required))
    }),
    phone: yup
      .string()
      .length(10, ({ length }) => intl.formatMessage(phoneMessages.format, { length }))
      .when('email', {
        is: (email: string) => isEmpty(email),
        then: (schema) => schema.required(intl.formatMessage(phoneMessages.required)),
        otherwise: (schema) => schema.optional()
      }),
    email: yup
      .string()
      .email(intl.formatMessage(emailMessages.format))
      .min(EMAIL_LENGTH.MIN, ({ min }) => intl.formatMessage(emailMessages.min, { min }))
      .max(EMAIL_LENGTH.MAX, ({ max }) => intl.formatMessage(emailMessages.max, { max }))
      .when('phone', {
        is: (phone: string) => isEmpty(phone),
        then: (schema) => schema.required(intl.formatMessage(emailMessages.required)),
        otherwise: (schema) => schema.optional()
      }),
    state: yup.string().optional(),
    gender: yup.string().optional()
  }

  return yup.object().shape(validations, [
    ['phone', 'email'],
    ['email', 'phone']
  ])
}
